/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import HeroBanner from 'bundles/shared-articles/components/ArticleHubHeroBanner';
import CategoryNavigation from 'bundles/unified-seo-content-common/components/hub-page/CategoryNavigation';
import ContentCards from 'bundles/unified-seo-content-common/components/hub-page/ContentCards';
import Layout from 'bundles/unified-seo-content-common/components/hub-page/Layout';
import RichText from 'bundles/unified-seo-content-common/components/rich-text/RichText';
import MainHubPageSEO from 'bundles/unified-seo-content-common/components/seo/MainHubPageSEO';
import { PAGE_TYPE, SEO_CONTENT_PAGE_SLUG, SEO_CONTENT_PAGE_TYPE } from 'bundles/unified-seo-content-common/constants';
import type { PageDataProps as IntermediatePageData } from 'bundles/unified-seo-content-common/hooks/useSeoContentMainHubPageQuery';
import { getPageLink } from 'bundles/unified-seo-content-common/utils';

import _t from 'i18n!nls/resources';

export type Props = {
  pageData: Omit<IntermediatePageData, 'error' | 'isHubPageDataLoading'>;
};

const styles = {
  rowsContainer: css`
    display: flex;
    flex-direction: column;
  `,
};

export const HubPage = ({ pageData }: Props) => {
  const {
    title,
    introduction,
    featuredImageUrl,
    featuredImageAlt,
    featuredContent,
    bottomContentJson,
    bottomContentLinks,
    featuredContentByCategoryList,
    navigationLinks,
  } = pageData;

  const missingRequiredFields =
    !featuredContentByCategoryList || !title || !introduction || !featuredImageUrl || !featuredContent;

  if (missingRequiredFields) {
    return null;
  }

  const pageType = SEO_CONTENT_PAGE_TYPE.resources;

  return (
    <React.Fragment>
      <Layout
        heroBannerComponent={
          <HeroBanner
            title={title}
            subtitle={introduction}
            image={{
              src: featuredImageUrl,
              alt: featuredImageAlt || _t('Hero Banner'),
            }}
          />
        }
        navigationComponent={
          <CategoryNavigation activeCategorySlug={SEO_CONTENT_PAGE_SLUG[pageType]} navigationLinks={navigationLinks} />
        }
        contentComponent={
          <div css={styles.rowsContainer}>
            <ContentCards
              contentList={featuredContent}
              imageLazyLoad={false}
              isFeaturedContentRow
              pageType={pageType}
            />
            {featuredContentByCategoryList?.map(
              (featuredContentByCategory) =>
                featuredContentByCategory?.title &&
                featuredContentByCategory.featuredContentCollection && (
                  <ContentCards
                    key={featuredContentByCategory.title}
                    title={featuredContentByCategory.title}
                    ctaLink={getPageLink({
                      slug: featuredContentByCategory.slug ?? '',
                      localizedCountryCode: featuredContentByCategory.countryCode,
                      pageType: PAGE_TYPE.categoryHub,
                      seoContentPageType: pageType,
                    })}
                    contentList={featuredContentByCategory.featuredContentCollection?.items}
                    imageLazyLoad={true}
                    pageType={pageType}
                  />
                )
            )}
            {bottomContentJson && <RichText content={bottomContentJson} links={bottomContentLinks} />}
          </div>
        }
      />
      <MainHubPageSEO pageData={pageData} pageType={pageType} />
    </React.Fragment>
  );
};

export default HubPage;
